import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';

export interface LcQuote {
  amountOut: string;
  feeAmount: string;
  priceImpactBps: number;
  niceMode?: {
    adjustedInput: string;
    cleanOutput: string;
    roundingSavings: string;
  };
}

interface UseLcQuoteReturn {
  quote: LcQuote | null;
  loading: boolean;
  error: string | null;
}

export function useLiquidityCacheQuote(
  poolId: string | undefined,
  direction: string | undefined,
  amount: string | undefined,
  niceMode?: boolean,
): UseLcQuoteReturn {
  const [quote, setQuote] = useState<LcQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchQuote = useCallback(async () => {
    if (!poolId || !direction || !amount || amount === '0') {
      setQuote(null);
      return;
    }

    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getLiquidityCacheQuote(poolId, direction, amount, niceMode);
      if (fetchIdRef.current !== id) return;
      setQuote(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to get quote');
      setQuote(null);
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [poolId, direction, amount, niceMode]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(fetchQuote, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      fetchIdRef.current++;
    };
  }, [fetchQuote]);

  return { quote, loading, error };
}
